import React from "react";
import { Stack, Inline } from "components/common/Stack";
import type { UnitDetails } from "./types";

interface HeuristicBannerProps {
  details: UnitDetails;
  onApply: () => void;
  onDismiss: () => void;
}

/**
 * Subtle "Suggested from column name" banner shown on first open when the
 * heuristic alias table resolves the column name to a unit AND the user has
 * not yet dismissed this banner during the current picker session.
 *
 * Hides while typing (parent owns visibility logic). Two actions: Apply
 * (commits + closes picker) and Dismiss (hides banner for this open). The
 * "Pick different" path is the unit list itself.
 */
export function HeuristicBanner({
  details,
  onApply,
  onDismiss,
}: HeuristicBannerProps) {
  return (
    <div
      role="region"
      aria-label="Suggested unit from column name"
      style={{
        background: "var(--color-accent-subtle)",
        border: "var(--size-border-hairline) solid var(--color-accent)",
        borderRadius: "var(--radius-app)",
        padding: "var(--spacing-sm) var(--spacing-md)",
      }}
    >
      <Stack gap="2xs">
        <span
          style={{
            fontSize: "var(--font-size-xx-small)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            color: "var(--color-accent)",
          }}
        >
          Suggested from column name
        </span>
        <Inline gap="xs" align="baseline">
          <strong style={{ fontWeight: 600 }}>
            {details.label ?? details.uri}
          </strong>
          {details.symbol && (
            <span
              style={{
                fontFamily: "var(--font-mono)",
                color: "var(--color-fg-muted)",
              }}
            >
              {details.symbol}
            </span>
          )}
          {details.quantity_kind_label && (
            <span
              style={{
                marginInlineStart: "auto",
                fontSize: "var(--font-size-xx-small)",
                color: "var(--color-fg-muted)",
                border: "var(--size-border-hairline) solid var(--color-border)",
                padding: "var(--spacing-2xs) var(--spacing-xs)",
                borderRadius: "var(--radius-app)",
              }}
            >
              {details.quantity_kind_label}
            </span>
          )}
        </Inline>
        <Inline gap="md">
          <button
            type="button"
            onClick={onApply}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: "var(--color-accent)",
              cursor: "pointer",
              fontSize: "var(--font-size-small)",
              fontFamily: "inherit",
            }}
          >
            Apply
          </button>
          <button
            type="button"
            onClick={onDismiss}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              color: "var(--color-fg-muted)",
              cursor: "pointer",
              fontSize: "var(--font-size-small)",
              fontFamily: "inherit",
            }}
          >
            Dismiss
          </button>
        </Inline>
      </Stack>
    </div>
  );
}
